import { message } from "antd";
import FileSaver from "file-saver";
import { INSTRUCTION_SUMMARY_QUERY_KEY } from "hooks/queries/useInstructionSummary";
import { GET_STATUS_WISE_COUNT_QUERY_KEY } from "hooks/queries/useStatusWiseCount";
import i18n from "i18n";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AuthorizedService } from "shared/axios";
import { PaginatedResponse } from "types";
import {
  AdditionalInstallationInformation,
  BarrierDetailsSchema,
  InstallationSchema,
  LicensePlates,
  MultiStatus,
  UpdatedPrice,
  UpdateInstallationRequestBodyDTO,
} from "types/installation";
import { INSTALLATION_DATA_REFETCH_INTERVAL } from "utils/global/constants";
import { GET_ATTACHMENTS_QUERY_KEY } from "./useAdminAttachment";

const { t } = i18n;

const endPoint = "/installation";
export const INSTALLATION_LIST_QUERY_KEY = "installation-list";

export const useInstallationListPaginated = (params) => {
  return useQuery<PaginatedResponse<InstallationSchema>>({
    queryKey: [INSTALLATION_LIST_QUERY_KEY, params],
    queryFn: () =>
      new AuthorizedService()
        .get(endPoint, params)
        .then((res: any) => res.data),
    refetchOnWindowFocus: true,
    refetchInterval: INSTALLATION_DATA_REFETCH_INTERVAL,
  });
};

export const useInstallationUpdate = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateInstallationRequestBodyDTO) =>
      new AuthorizedService().put(`${endPoint}/${id}`, data).then((res) => res),
    onSuccess: () => {
      queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
    },
  });
};

export const useInstallationMultiStatusUpdate = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: Record<MultiStatus, boolean>) =>
      new AuthorizedService()
        .put(`${endPoint}/${id}/change-multi-status`, data)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("warnings:statusSuccess"));
      },
      onError: () => {
        message.error(t("warnings:error"));
      },
      onSettled: () => {
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
        queryClient.invalidateQueries(GET_STATUS_WISE_COUNT_QUERY_KEY);
        queryClient.invalidateQueries(INSTRUCTION_SUMMARY_QUERY_KEY);
      },
    }
  );
};

type AdditionalInformation = {
  additionalInformation: Partial<AdditionalInstallationInformation>;
  licensePlates?: Partial<LicensePlates>;
  updatedPrice?: UpdatedPrice;
  barrierDetails?: BarrierDetailsSchema;
  setupDate?: string;
  setupTime?: string;
  pickupDate?: string;
  updatedDateRangeFrom?: string;
  updatedDateRangeTo?: string;
  setupDoneDateTime?: string;
};

export const useInstallationAdditionalInformationUpdate = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: AdditionalInformation) =>
      new AuthorizedService()
        .put(`${endPoint}/${id}/additional-information`, data)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("general:updatedSuccess"));
      },
      onError: () => {
        message.error(t("general:updateFail"));
      },
      onSettled: () => {
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
        queryClient.invalidateQueries(GET_STATUS_WISE_COUNT_QUERY_KEY);
      },
    }
  );
};

type AdminInformation = {
  notice?: string | null;
};

export const useInstallationAdminInformationUpdate = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: AdminInformation) =>
      new AuthorizedService()
        .put(`${endPoint}/${id}/admin-information`, data)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("general:updatedSuccess"));
      },
      onError: () => {
        message.error(t("general:updateFail"));
      },
      onSettled: () => {
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
      },
    }
  );
};

type WorkerInformation = {
  workerNotice?: string | null;
  workerInstructionNumber?: number | null;
};

export const useInstallationWorkerInformationUpdate = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: WorkerInformation) =>
      new AuthorizedService()
        .put(`${endPoint}/${id}/worker-information`, data)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("general:updatedSuccess"));
      },
      onError: () => {
        message.error(t("general:updateFail"));
      },
      onSettled: () => {
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
      },
    }
  );
};

type ChangeInvoiceStatusRequestBody =
  | {
      changeInvoiceUploadedByClient: true;
    }
  | {
      changeInvoicePaidByClient: true;
    }
  | {
      changeTaxBooking: true;
    };

export const useInstallationInvoiceStatusUpdate = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ChangeInvoiceStatusRequestBody) =>
      new AuthorizedService()
        .put(`${endPoint}/${id}/change-invoice-status`, data)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("general:updatedSuccess"));
      },
      onError: () => {
        message.error(t("general:updateFail"));
      },
      onSettled: () => {
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
        queryClient.invalidateQueries(GET_STATUS_WISE_COUNT_QUERY_KEY);
      },
    }
  );
};

type AssignInstallationRequestBody =
  | {
      setupAssignedTo: string | null;
    }
  | {
      pickupAssignedTo: string | null;
    };

export const useInstallationAssign = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: AssignInstallationRequestBody) =>
      new AuthorizedService()
        .put(`${endPoint}/${id}/assign-installation`, data)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("general:updatedSuccess"));
      },
      onError: () => {
        message.error(t("general:updateFail"));
      },
      onSettled: () => {
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
      },
    }
  );
};

type WorkerOrderRequestBody =
  | {
      setupOrderNumber: number | null;
    }
  | {
      pickupOrderNumber: number | null;
    };

export const useInstallationWorkerOrderNumberUpdate = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: WorkerOrderRequestBody) =>
      new AuthorizedService()
        .put(`${endPoint}/${id}/worker-order-number`, data)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("general:updatedSuccess"));
      },
      onError: () => {
        message.error(t("general:updateFail"));
      },
      onSettled: () => {
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
      },
    }
  );
};

export const useInstallationExport = () => {
  return useMutation({
    mutationFn: () =>
      new AuthorizedService()
        .getBlob(`${endPoint}/export`)
        .then((res: any) => res as Blob),
    onSuccess: (blob: Blob) => {
      const fileName = `orders-export-${new Date().toISOString().slice(0, 10)}.zip`;
      FileSaver.saveAs(blob, fileName);
    },
    onError: () => {
      message.error(t("warnings:error"));
    },
  });
};

export const useInstallationLogPDFGeneration = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    () =>
      new AuthorizedService()
        .post(`${endPoint}/${id}/installation-log-pdf`, null)
        .then((res: any) => res),
    {
      onSuccess: () => {
        message.success(t("general:createdSuccess"));
      },
      onError: () => {
        message.error(t("general:createFail"));
      },
      onSettled: () => {
        queryClient.invalidateQueries([GET_ATTACHMENTS_QUERY_KEY, id]);
        queryClient.invalidateQueries(INSTALLATION_LIST_QUERY_KEY);
      },
    }
  );
};
